import { Card, Flex, Form, InputNumber, Radio, Select, Space, Button, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  NODE_TYPES,
  OPERATION_TYPES,
  VALUE_TYPES,
  CHILDREN_RULES,
} from './constants';

const { Text } = Typography;

const getValueTypeLabel = (valueType) => VALUE_TYPES.find((v) => v.value === valueType)?.label ?? valueType;

/**
 * Editor de un nodo de expresión (operación o valor). Recursivo para hijos.
 */
export const ExpressionNodeEditor = ({ node, onChange, onRemove, canRemove = true, isRoot = false, depth = 0 }) => {
  const nodeType = node?.nodeType ?? 'operation';
  const operationType = node?.operationType ?? 'add';
  const children = node?.children ?? [];
  const rule = CHILDREN_RULES[operationType];
  const minChildren = rule?.min ?? 0;
  const exactChildren = rule?.exact ?? null;
  const canAddChild = exactChildren == null ? true : children.length < exactChildren;
  const requiredCount = exactChildren ?? minChildren;
  const showRemove = !isRoot && onRemove && canRemove;

  const handleNodeTypeChange = (newType) => {
    if (newType === 'operation') {
      onChange({
        ...node,
        nodeType: 'operation',
        operationType: 'add',
        order: node?.order ?? 1,
        children: [
          { nodeType: 'value', valueType: 'insured_value', order: 1, valueName: 'Valor asegurado' },
          { nodeType: 'value', valueType: 'rate', order: 2, valueName: 'Tasa' },
        ],
        valueType: undefined,
        valueName: undefined,
        fixedValue: undefined,
      });
    } else {
      onChange({
        ...node,
        nodeType: 'value',
        valueType: 'insured_value',
        valueName: 'Valor asegurado',
        order: node?.order ?? 1,
        operationType: undefined,
        children: undefined,
      });
    }
  };

  const handleOperationTypeChange = (opType) => {
    const rule = CHILDREN_RULES[opType];
    let newChildren = [...(node.children ?? [])];
    const exact = rule?.exact;
    const min = rule?.min ?? 0;
    if (exact != null && newChildren.length !== exact) {
      if (newChildren.length < exact) {
        while (newChildren.length < exact) {
          newChildren.push({
            nodeType: 'value',
            valueType: 'insured_value',
            order: newChildren.length + 1,
            valueName: 'Valor asegurado',
          });
        }
      } else {
        newChildren = newChildren.slice(0, exact).map((c, i) => ({ ...c, order: i + 1 }));
      }
    } else if (newChildren.length < min) {
      while (newChildren.length < min) {
        newChildren.push({
          nodeType: 'value',
          valueType: 'insured_value',
          order: newChildren.length + 1,
          valueName: 'Valor asegurado',
        });
      }
    }
    newChildren = newChildren.map((c, i) => ({ ...c, order: i + 1 }));
    onChange({ ...node, operationType: opType, children: newChildren });
  };

  const handleChildChange = (index, newChild) => {
    const newChildren = [...children];
    newChildren[index] = newChild;
    onChange({ ...node, children: newChildren });
  };

  const handleAddChild = () => {
    if (!canAddChild) return;
    const newChild = {
      nodeType: 'value',
      valueType: 'insured_value',
      order: children.length + 1,
      valueName: 'Valor asegurado',
    };
    onChange({ ...node, children: [...children, newChild] });
  };

  const handleRemoveChild = (index) => {
    const newChildren = children.filter((_, i) => i !== index).map((c, i) => ({ ...c, order: i + 1 }));
    onChange({ ...node, children: newChildren });
  };

  const handleValueTypeChange = (valueType) => {
    const label = getValueTypeLabel(valueType);
    onChange({
      ...node,
      valueType,
      valueName: valueType === 'fixed' ? (node.valueName ?? '') : label,
      fixedValue: valueType === 'fixed' ? node.fixedValue : undefined,
    });
  };

  const handleValueNameChange = (e) => {
    onChange({ ...node, valueName: e?.target?.value ?? '' });
  };

  const handleFixedValueChange = (value) => {
    onChange({ ...node, fixedValue: value });
  };

  const marginLeft = depth * 16;

  return (
    <Card size='small' style={{ marginLeft, marginTop: 8 }}>
      <Flex justify='space-between' align='flex-start'>
        <Flex vertical gap='small' style={{ flex: 1 }}>
          <Text type='secondary' style={{ fontSize: '0.85em' }}>
            Tipo de nodo
          </Text>
          <Radio.Group
            value={nodeType}
            onChange={(e) => handleNodeTypeChange(e.target.value)}
            options={NODE_TYPES}
          />
          {nodeType === 'operation' && (
            <>
              <Text type='secondary' style={{ fontSize: '0.85em', marginTop: 8 }}>
                Operación
              </Text>
              <Select
                value={operationType}
                onChange={handleOperationTypeChange}
                options={OPERATION_TYPES}
                style={{ width: '100%' }}
              />
              <Text type='secondary' style={{ fontSize: '0.85em', marginTop: 8 }}>
                Operandos ({children.length}{exactChildren != null ? ` de ${exactChildren}` : ` (mín. ${minChildren})`})
              </Text>
              {children.map((child, index) => (
                <ExpressionNodeEditor
                  key={index}
                  node={child}
                  onChange={(newChild) => handleChildChange(index, newChild)}
                  onRemove={() => handleRemoveChild(index)}
                  canRemove={exactChildren != null ? children.length > exactChildren : children.length > minChildren}
                  isRoot={false}
                  depth={depth + 1}
                />
              ))}
              {canAddChild && (
                <Button type='dashed' icon={<PlusOutlined />} onClick={handleAddChild} style={{ marginTop: 8 }}>
                  Añadir operando
                </Button>
              )}
            </>
          )}
          {nodeType === 'value' && (
            <>
              <Text type='secondary' style={{ fontSize: '0.85em', marginTop: 8 }}>
                Tipo de valor
              </Text>
              <Select
                value={node?.valueType ?? 'insured_value'}
                onChange={handleValueTypeChange}
                options={VALUE_TYPES}
                style={{ width: '100%' }}
              />
              {node?.valueType === 'fixed' && (
                <Flex vertical gap='small' style={{ marginTop: 8 }}>
                  <Form.Item label='Nombre' style={{ marginBottom: 0 }}>
                    <Space.Compact style={{ width: '100%' }}>
                      <input
                        type='text'
                        value={node.valueName ?? ''}
                        onChange={handleValueNameChange}
                        placeholder='Ej. Cargo fijo'
                        style={{
                          width: '100%',
                          padding: '4px 11px',
                          border: '1px solid #d9d9d9',
                          borderRadius: 6,
                        }}
                      />
                    </Space.Compact>
                  </Form.Item>
                  <Form.Item label='Valor numérico' style={{ marginBottom: 0 }}>
                    <InputNumber
                      min={0}
                      value={node.fixedValue}
                      onChange={handleFixedValueChange}
                      style={{ width: '100%' }}
                      placeholder='0'
                    />
                  </Form.Item>
                </Flex>
              )}
            </>
          )}
        </Flex>
        {showRemove && (
          <Button type='text' danger icon={<DeleteOutlined />} onClick={onRemove} />
        )}
      </Flex>
    </Card>
  );
};
